import {AfterViewInit, Component, OnInit, signal} from '@angular/core';
import {Subject, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {InterestService} from '../../shared/services/interest.service';
import {TagService} from '../../api/services/tag.service';
import {Tag} from '../../api/models/tag';
import {empty} from '../../functions';
import {ModalService} from '../../shared/services/modal.service';
import {AdminService} from '../../shared/services/admin.service';
import {DomainService} from '../../shared/services/domain.service';
import {alertMessages, AlertService} from '../../shared/services/alert.service';
import {ModalAdminStudentprofileComponent} from './modal-admin-studentprofile/modal-admin-studentprofile.component';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {StudentService} from '../../shared/services/student.service';
import {
  ProfileLookingForUtils,
  ProfileTypeUtils,
} from '../../shared/utils/student-profile.utils';

@Component({
    selector: 'app-admin-studentprofiles',
    templateUrl: './admin-studentprofiles.component.html',
    styleUrls: ['./admin-studentprofiles.component.scss'],
    standalone: false
})
export class AdminStudentprofilesComponent implements OnInit, AfterViewInit {

  filterOpen = false;

  public view = {
    filters: {
      sites: <any>[],
      queryParams: <any>{
        SearchTerm: null,
        'Address.City': null,
        'Address.Latitude': null,
        'Address.Longitude': null,
        // BE binds these as a nested Address model and trips [Required] on
        // Street/Number/PostalCode/Country whenever ANY Address.* is sent.
        // We only filter on city + lat/lng, so we leave these as null until
        // a location is picked, then backfill with a placeholder space (see
        // setAddressRequiredPlaceholders) — null values are stripped by the
        // table component before the request goes out.
        'Address.Street': null,
        'Address.Number': null,
        'Address.PostalCode': null,
        'Address.Country': null,
        AreaOfInterests: [],
        ProfileType: null,
        LookingFor: null,
        EducationSearchTerm: null,
        ReferenceCode: null,
        MaximumDistance: null,
        Tags: [],
        SortField: 'createdOn',
        SortType: 'Descending',
        }
    },
    filterOptions: {
      areaOfInterests: <any> [],
      sites: <any> [],
      lookingFor: <any> [],
      profileTypes: <any> [],
      radiusOptions: [10, 20, 30, 50, 100],
    },
  };

  tagSuggestions = signal<Tag[]>([]);

  public configTable = {
    hasPagination: true,
    columns: [
      {
        header_name: 'Photo',
        name: 'avatarUri',
        type: 'image',
        class: 'img-64'
      },
      {
        header_name: 'First name',
        name: 'firstName',
        type: 'text'
      },
      {
        header_name: 'Last name',
        name: 'lastName',
        type: 'text'
      },
      {
        header_name: 'Email',
        name: 'emailAddress',
        type: 'text'
      },
      {
        header_name: 'City',
        name: 'homeAddress',
        subName: 'city',
        type: 'text'
      },
      {
        header_name: 'Country',
        name: 'homeAddress',
        subName: 'country',
        type: 'text'
      },
      {
        header_name: 'Profile strength',
        name: 'profileStrength',
        type: 'text'
      },
      {
        header_name: 'Field of interest',
        name: 'areaOfInterests',
        type: 'array',
        field: 'name'
      },
      {
        header_name: 'Currently I am',
        name: 'profileType',
        type: 'text'
      },
      {
        header_name: 'Looking for',
        name: 'lookingFor',
        type: 'text'
      },
      {
        header_name: 'Reference code',
        name: 'referenceCode',
        type: 'text'
      },
      {
        header_name: 'Tags',
        name: 'tags',
        type: 'array',
        field: ''
      },
    ],
    endpoint: 'studentprofile',
    data: <any> [],
    buttons: [
      {
        class: 'btn btn-info',
        event: 'profile',
        text: 'Profile'
      },
      {
        class: 'btn btn-danger',
        event: 'delete',
        text: 'Delete'
      },
      {
        class: 'btn btn-warning',
        event: 'edit',
        text: 'Edit'
      }
    ],
    class: 'table-responsive',
    reloadListener: new Subject(),
    queryParams: this.view.filters.queryParams

  };

  /**
   * Constructor
   *
   * @param interests
   */
  constructor(public modal:ModalService,
              private adminService: AdminService,
              public studentService:StudentService,
              private domain: DomainService,
              public interest: InterestService,
              private alertService: AlertService,
              private router: Router,
              private route:ActivatedRoute,
              private tagService: TagService) {


  }

  /**
   * Ng on init
   */
  ngOnInit(): void {

    this.view.filterOptions.lookingFor = ProfileLookingForUtils.getOptions();
    this.view.filterOptions.profileTypes = ProfileTypeUtils.getOptions();


    // Set area of interest filter options.
    // Reassign a NEW array instead of pushing: <app-filter-panel> reads its
    // options via a signal input(), which only reacts to a new reference — an
    // in-place push into the existing array leaves the panel showing nothing.
    this.interest.apiGetAreaOfInterests().subscribe( (result:any) => {
      if (!empty(result)) {
        this.view.filterOptions.areaOfInterests = result.data.map(
          (item:any) => ({value: item.name, label: item.name})
        );
      }
    });

    // Set site filter options
    const sites = this.domain.getAllDomains();
    if (!empty(sites)) {

      sites.map( (item:any) => {
        this.view.filterOptions.sites.push({value: item.id, label: item.url + ' (' + item.code + ')'});
      });

    }

    // Preload tag suggestions for the Tags filter typeahead
    this.tagService
      .getTags()
      .pipe(catchError(() => of([])))
      .subscribe((result: any) => {
        const tags: Tag[] = result?.data ?? result?.items ?? result ?? [];
        this.tagSuggestions.set(tags);
      });

  }

  ngAfterViewInit() {

    this.route.params.subscribe( (params: Params) => {

      if (params.id) {
        this.updateStudentprofile(params);
      }

    });

  }

  /**
   * Update a single student profile
   *
   * @param id
   */
  updateStudentprofile($data:any) {

    const modalOptions = {
      backdrop: 'static',
      size: 'xl'
    };

    const data = {
      entity: $data
    };

    this.modal.Modal(ModalAdminStudentprofileComponent, '', '', data, [], modalOptions).then( (result) => {

      if (result === true) {

        this.alertService.success(alertMessages.successDataSaved);

        this.configTable.reloadListener.next(true);
      }

    }).catch( (error) => {

    });

  }


  /**
   * Delete a single student profile
   *
   * @param id
   */
  deleteStudentprofile(id:string) {

    this.modal.Confirm('Delete', 'Are you sure you want to delete this?').then( (confirm) => {
      if (confirm === true) {

        this.adminService.apiDeleteStudentprofile(id).subscribe( (result) => {

          // Reload data
          this.configTable.reloadListener.next(true);

        });

      }
    }).catch( (error) => {

    });

  }

  /**
   * Handle table button event
   *
   * @param $event
   */
  handleButtonEvent($event:any) {

    if (!empty($event['event'])) {

      switch ($event['event']) {

        case 'profile':
          this.openStudentprofile($event['data']['id']);
          break;

        case 'delete':
          this.deleteStudentprofile($event['data']['id']);
          break;

        case 'edit':
          this.updateStudentprofile($event['data']);
          break;
      }

    }

  }

  /**
   * Open company profile (edit) page
   *
   * @param $id
   */
  openStudentprofile($id: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/company/students/' + $id]));
    window.open(url, '_blank');
  }

  reloadData() {

    this.setQueryParams();

    this.configTable.reloadListener.next(true);
  }

  setQueryParams() {
    this.configTable.queryParams = this.view.filters.queryParams
  }

  resetFilters() {

    this.view.filters.queryParams = {
      SearchTerm: null,
      'Address.City': null,
      'Address.Latitude': null,
      'Address.Longitude': null,
      'Address.Street': null,
      'Address.Number': null,
      'Address.PostalCode': null,
      'Address.Country': null,
      AreaOfInterests: [],
      ProfileType: null,
      LookingFor: null,
      EducationSearchTerm: null,
      ReferenceCode: null,
      MaximumDistance: null,
      Tags: [],
      SortField: 'createdOn',
      SortType: 'Descending',
    };

    this.reloadData();
  }

  applyFilters()
  {
    this.reloadData();
  }

  /**
   * Google Places autocomplete handler for the location filter.
   * Captures city + coordinates so the BE can apply MaximumDistance correctly.
   */
  onLocationAddressChange($event: any): void {

    if (!$event) {
      this.clearLocationFilter();
      return;
    }

    // Resolve city name from address_components (fall back to formatted name)
    let city: string | null = null;
    if (Array.isArray($event.address_components)) {
      const localityComponent = $event.address_components.find((c: any) =>
        Array.isArray(c.types) && (c.types.includes('locality') || c.types.includes('postal_town'))
      );
      city = localityComponent?.long_name ?? $event.address_components[0]?.long_name ?? null;
    }

    let lat: number | null = null;
    let lng: number | null = null;
    if ($event.geometry?.location) {
      const rawLat = $event.geometry.location.lat;
      const rawLng = $event.geometry.location.lng;
      lat = typeof rawLat === 'function' ? rawLat() : rawLat;
      lng = typeof rawLng === 'function' ? rawLng() : rawLng;
    }

    this.view.filters.queryParams['Address.City'] = city;
    this.view.filters.queryParams['Address.Latitude'] = lat;
    this.view.filters.queryParams['Address.Longitude'] = lng;
    this.setAddressRequiredPlaceholders(true);

    this.reloadData();
  }

  /**
   * Called when the raw text is cleared; wipes city/lat/lng so the filter resets.
   */
  onLocationInput(value: string): void {
    if (!value) {
      this.clearLocationFilter();
      this.reloadData();
    } else {
      // Keep the typed value around so we still send it even without a Google match
      this.view.filters.queryParams['Address.City'] = value;
      this.setAddressRequiredPlaceholders(true);
    }
  }

  private clearLocationFilter(): void {
    this.view.filters.queryParams['Address.City'] = null;
    this.view.filters.queryParams['Address.Latitude'] = null;
    this.view.filters.queryParams['Address.Longitude'] = null;
    this.setAddressRequiredPlaceholders(false);
  }

  /**
   * Workaround for the BE binding the `Address.*` query params as a nested
   * Address model with [Required] on Street/Number/PostalCode/Country. We
   * only filter on City + lat/lng, but the moment any one of those is sent
   * MVC tries to bind the whole Address and validation fails. Pad the four
   * required fields with `-` so RequiredAttribute (which trims before
   * checking length) accepts them; null when the filter is cleared so the
   * table strips them before the request.
   */
  private setAddressRequiredPlaceholders(active: boolean): void {
    const value = active ? '-' : null;
    this.view.filters.queryParams['Address.Street'] = value;
    this.view.filters.queryParams['Address.Number'] = value;
    this.view.filters.queryParams['Address.PostalCode'] = value;
    this.view.filters.queryParams['Address.Country'] = value;
  }

}
